import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { materialSymbolsOutlined } from '@lib/enums/common';
import { Modals } from '@lib/enums/modals';
import Trans from '@lib/components/Trans/Trans';
import { IconName } from '@lib/components/Modal/enums';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import useModalManagement from '@lib/hooks/useModalManagement';
import SectorViewBottomActions from 'routes/Sectors/Sector/SectorViewBottomActions/SectorViewBottomActions';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import SectorUsers from './SectorUsers/SectorUsers';
import SectorSites from './SectorSites/SectorSites';
import SectorMenuBar from './SectorMenuBar/SectorMenuBar';
import SectorDetails from './SectorDetails/SectorDetails';
import useActions from './utils/useActions';
import { SectorViewMode } from './enums';

const modals = [
  Modals.DestroyModal,
  Modals.UserDeleteModal,
  Modals.UserFormModal,
];

type Props = {
  mode: SectorViewMode;
};

export default function SectorViewContainer({ mode }: Props) {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { pathname, search } = useLocation();
  const useModalManagementResult = useModalManagement(modals);
  const useActionsResult = useActions({
    id,
    useModalManagementResult,
    mode,
    pathname,
    search,
  });
  const {
    destroyLoading,
    isUsersManagementLocked,
    isViewOnly,
    onConfirmedDelete,
    onDeleteHandler,
    onEditHandler,
    sectorQueryHookResult,
    sitesQueryHookResult,
    usersQueryHookResult,
  } = useActionsResult;

  const { data, firstLoading, error } = sectorQueryHookResult;
  const name = _get(data, 'name', '');
  const readOnly = _get(data, 'readOnly', false);
  const canDestroy = _get(data, 'canDestroy.value');
  const sitesCount = _get(data, 'sitesCount', 0);
  const usersCount = _get(data, 'usersCount', 0);

  const pageError =
    error || usersQueryHookResult.error || sitesQueryHookResult.error;

  if (pageError) {
    return (
      <ErrorPage
        status={getStatusFromError(pageError)}
        description={getDescriptionFromError(pageError)}
      />
    );
  }

  return (
    <>
      <BreadCrumbs
        data={getBreadCrumbsDataByKey({
          pathname,
          t,
          entity: data || undefined,
        })}
      />
      <PageBody withoutHeader>
        {/* Outside the loading branch so the tabs are on screen from the first
            paint and stay mounted across switches — the sliding active
            indicator can't animate across a remount either. */}
        <SectorMenuBar
          active={pathname}
          id={id}
          isUsersManagementLocked={isUsersManagementLocked}
          sitesCount={sitesCount}
          usersCount={usersCount}
        />
        {firstLoading ? (
          <CircleLoader />
        ) : (
          <DashboardContentMaxWidth disabled={mode !== SectorViewMode.Details}>
            <WhiteBox>
              <DashboardTitleHeader
                title={name || ''}
                actions={
                  readOnly || isViewOnly ? (
                    <ViewOnlyChip key="view" />
                  ) : (
                    <Button
                      buttonText={t('edit-sector')}
                      buttonType={ButtonTypes.primaryFilled}
                      onClick={onEditHandler}
                      leftIcon="edit"
                      leftIconClassName={materialSymbolsOutlined}
                    />
                  )
                }
              />
              {mode === SectorViewMode.Details && (
                <SectorDetails
                  data={data}
                  onEditHandler={onEditHandler}
                  isViewOnly={isViewOnly || readOnly}
                />
              )}
              {mode === SectorViewMode.Sites && (
                <SectorSites
                  sitesQueryHookResult={sitesQueryHookResult}
                  onEditHandler={onEditHandler}
                />
              )}
              {mode === SectorViewMode.Users && (
                <SectorUsers
                  isViewOnly={false}
                  useActionsResult={useActionsResult}
                  useModalManagementResult={useModalManagementResult}
                />
              )}
            </WhiteBox>
            {mode === SectorViewMode.Details && !isViewOnly && (
              <SectorViewBottomActions
                canDestroy={canDestroy}
                onDeleteHandler={onDeleteHandler}
              />
            )}
          </DashboardContentMaxWidth>
        )}
      </PageBody>
      <ConfirmationModal
        isOpen={useModalManagementResult.isModalOpened(Modals.DestroyModal)}
        loading={destroyLoading}
        onClose={() => useModalManagementResult.closeModal(Modals.DestroyModal)}
        onConfirm={onConfirmedDelete}
        useProgress
        title={t('are-you-sure')}
        description={<Trans i18nKey="delete-sector-text" values={{ name }} />}
        confirmButtonLabel={t('yes-delete')}
        icon={IconName.Delete}
        cancelButtonLabel={t('cancel')}
      />
    </>
  );
}
